import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ImmediateOnDemandWaiting from "../../common/immediateOnDemand/ImmediateOnDemandWaiting";
import ImmediateOnDemandThankYou from "../../common/immediateOnDemand/ImmediateOnDemandThankYou";
import { getAppointmentId } from "../../common/immediateOnDemand/matchingUtils";
import immediateOnDemandRealtime from "../../../services/immediateOnDemandRealtime";
import useStoppableSound from "../../common/immediateOnDemand/useStoppableSound";
import "../../common/immediateOnDemand/immediateOnDemand.css";

const WAIT_OPTIONS = [
  { label: "15 Minutes", value: "15" },
  { label: "30 Minutes", value: "30" },
];

export default function MediumImmediateOnDemand() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const audioRef = useRef(null);
  const [step, setStep] = useState("settings");
  const [signedIntoZoom, setSignedIntoZoom] = useState(false);
  const [waitTime, setWaitTime] = useState("");
  const [customMinutes, setCustomMinutes] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [eligibility, setEligibility] = useState({
    checked: false,
    eligible: false,
    message: "",
  });
  const source = searchParams.get("source");
  const sourceAppointmentId = searchParams.get("sourceAppointmentId");
  const isBackfill = source === "backfill" && sourceAppointmentId;

  const [isTestSoundPlaying, setIsTestSoundPlaying] = useState(false);

  const stopTestSound = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setIsTestSoundPlaying(false);
  };

  // Toggle: first click plays the chime, second click stops it.
  const toggleTestSound = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isTestSoundPlaying) {
      stopTestSound();
      return;
    }

    audio.currentTime = 0;
    audio.volume = 0.25;
    audio
      .play()
      .then(() => setIsTestSoundPlaying(true))
      .catch(() => setIsTestSoundPlaying(false));
  };

  // Reset the button when the chime finishes on its own, and stop the
  // test sound if the user leaves the settings screen (e.g. Go On-Demand).
  useEffect(() => {
    if (step !== "settings") return undefined;
    const audio = audioRef.current;
    if (!audio) return undefined;

    const handleEnded = () => setIsTestSoundPlaying(false);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.currentTime = 0;
      setIsTestSoundPlaying(false);
    };
  }, [step]);
 

  const isBlockedByUpcomingAppointment =
    eligibility.checked && eligibility.eligible === false;

  useEffect(() => {
    let isMounted = true;

    immediateOnDemandRealtime
      .getEligibility({
        ...(isBackfill ? { source: "backfill", sourceAppointmentId } : {}),
      })
      .then((response) => {
        if (!isMounted) return;

        setEligibility({
          checked: true,
          eligible:
            response &&
            response.eligible === true &&
            response.hasAppointmentWithinThirtyMinutes !== true,
          message: response && response.message ? response.message : "",
        });
      })
      .catch(() => {
        if (!isMounted) return;

        setEligibility({
          checked: true,
          eligible: false,
          message: "Unable to check on-demand availability. Please try again.",
        });
      });

    return () => {
      isMounted = false;
    };
  }, [isBackfill, sourceAppointmentId]);

  const goToWaitingRoom = () => {
    if (!eligibility.checked || isBlockedByUpcomingAppointment) return;

    setStep("waiting");
  };
  const isCustomWaitTime = waitTime && waitTime !== "15" && waitTime !== "30";

  const handleCustomMinutesChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    if (raw === "") {
      setCustomMinutes("");
      setWaitTime("");
      return;
    }
    const num = parseInt(raw, 10);
    if (num >= 1 && num <= 60) {
      setCustomMinutes(String(num));
      setWaitTime(String(num));
    }
  };

  const storeImmediateAppointmentId = (data) => {
    const appointmentId = getAppointmentId(data);
    if (appointmentId) {
      localStorage.setItem("immediate_session_appointment_id", appointmentId);
    }
  };
  useEffect(() => {
    console.log("Step changed to:", step);
  }, [step]);

  // Confirmation sound (ringNew.wav, ~2 min): stops on any click, key press,
  // hover over a button/link, tab switch, or leaving the screen.
  useStoppableSound(audioRef, step === "thankyou");

  if (step === "waiting") {
    return (<>
       <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />
      <ImmediateOnDemandWaiting
        participantRole="provider"
        settings={{ waitTime, signedIntoZoom, waitOptions: WAIT_OPTIONS }}
        queueSource={isBackfill ? "backfill" : undefined}
        sourceAppointmentId={isBackfill ? sourceAppointmentId : undefined}
        exitPath="/service-entry"
        onMatched={(data) => {
          setMatchData(data);
          storeImmediateAppointmentId(data);
          setStep("thankyou");
        }}
        title="Hold on, we are searching for your match."
        matchedPath={(matchData) => {
          const appointmentId = matchData && matchData.appointmentId;
          return appointmentId
            ? `/thankyou?from=goondemand&appointment_id=${appointmentId}`
            : "/thankyou?from=goondemand";
        }}
      />
 
    </>);
  }

  if (step === "thankyou") {
    return (
      <>
      <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />
      <ImmediateOnDemandThankYou
        matchData={matchData}
        participantRole="provider"
        onDone={() => navigate("/appointments")}
      />
      </>
    );
  }


  return (
    <div className="immediate-ondemand-page">
      <audio ref={audioRef} src="/ring/ringNew.wav" preload="auto" />

      <div className="immediate-ondemand-card iod-v2-card">
        {/* Header */}
        <div className="iod-v2-header">
          <div className="iod-v2-header-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>
          <div>
            <h2 className="iod-v2-title">{isBackfill ? "Backfill OnDemand" : "Client OnDemand"}</h2>
            <p className="iod-v2-subtitle">Get matched for an immediate session.</p>
          </div>
        </div>

        {/* Zoom row */}
        <div
          className={`iod-v2-row${signedIntoZoom ? " iod-v2-row--checked" : ""}`}
          onClick={() => setSignedIntoZoom(!signedIntoZoom)}
          role="checkbox"
          aria-checked={signedIntoZoom}
          tabIndex={0}
          onKeyDown={(e) => e.key === " " && setSignedIntoZoom(!signedIntoZoom)}
        >
          <div className={`iod-v2-row-icon${signedIntoZoom ? " iod-v2-row-icon--active" : ""}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              {signedIntoZoom && <polyline points="8 12 11 15 16 9" />}
            </svg>
          </div>
          <div>
            <p className="iod-v2-row-title">Sign in to Zoom</p>
            <p className="iod-v2-row-sub">Be ready to connect when matched.</p>
          </div>
        </div>

        {/* Wait time row */}
        <div className="iod-v2-row iod-v2-row--static">
          <div className="iod-v2-row-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="iod-v2-row-body">
            <p className="iod-v2-row-title">Set how long you'll wait</p>
            <div className="iod-v2-pills">
              {WAIT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`iod-v2-pill${waitTime === option.value ? " iod-v2-pill--active" : ""}`}
                  onClick={() => { setWaitTime(option.value); setShowCustomInput(false); setCustomMinutes(""); }}
                >
                  {option.label}
                </button>
              ))}
              {showCustomInput ? (
                <div className="iod-v2-custom-wrap">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="iod-v2-custom-input"
                    value={customMinutes}
                    onChange={handleCustomMinutesChange}
                    placeholder="1–60"
                    autoFocus
                    maxLength={2}
                  />
                  <span className="iod-v2-custom-unit">min</span>
                </div>
              ) : (
                <button
                  type="button"
                  className={`iod-v2-pill${isCustomWaitTime ? " iod-v2-pill--active" : ""}`}
                  onClick={() => { setShowCustomInput(true); setWaitTime(""); setCustomMinutes(""); }}
                >
                  {isCustomWaitTime ? `${waitTime} Min` : "Other"}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stay near device row */}
        <div className="iod-v2-row iod-v2-row--static">
          <div className="iod-v2-row-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
              <line x1="12" y1="18" x2="12.01" y2="18" />
            </svg>
          </div>
          <div>
            <p className="iod-v2-row-title">Stay near your device</p>
            <p className="iod-v2-row-sub">We'll chime the moment you match.</p>
          </div>
        </div>

        {/* When you match box */}
        <div className="iod-v2-match-box">
          <div className="iod-v2-match-box-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <span>When you match</span>
          </div>
          <p>Hear a chime, then jump to Appointments for your Zoom link.</p>
          <button
            type="button"
            className={`iod-v2-sound-btn${isTestSoundPlaying ? " is-playing" : ""}`}
            onClick={toggleTestSound}
            aria-pressed={isTestSoundPlaying}
          >
            {isTestSoundPlaying ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
            {isTestSoundPlaying ? "Stop sound" : "Test sound"}
          </button>
        </div>

        {/* Eligibility message */}
        {isBlockedByUpcomingAppointment && eligibility.message ? (
          <p className="immediate-ondemand-note">{eligibility.message}</p>
        ) : null}

        {/* CTA */}
        <button
          type="button"
          className="iod-v2-cta"
          onClick={goToWaitingRoom}
          disabled={!signedIntoZoom || !waitTime || !eligibility.checked || isBlockedByUpcomingAppointment}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
          Go On-Demand
        </button>

        {/* Footer */}
        <p className="iod-v2-footer">
          No match in time? Extend your wait or leave the queue — anytime.{" "}
          <button
            type="button"
            className="iod-v2-cancel-link"
            onClick={() => navigate("/appointments")}
          >
            Cancel
          </button>
        </p>
      </div>
    </div>
  );
}
